import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import {
  Avatar,
  Box,
  List,
  ListItemButton,
  MenuItem,
  Pagination,
  Select,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { appSelector } from "../redux/selector";
import { getSourcesAction, selectedSourceAction } from "../redux/action";
import LoadingSkeleton from "../loadingSkeletons/LoadingList";

const Sourceslist = ({ searchTerm = "", days }) => {
  const dispatch = useDispatch();
  const { sourcesList, sourcesLoading } = useSelector(appSelector);
  const [originalSourceNames, setOriginalSourceNames] = useState([]);
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [page, setPage] = useState(1)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const [totalPages, setTotalPages] = useState(1)

  // Cache for API calls to prevent redundant requests
  const cache = useRef(new Map());
  const lastFetchParams = useRef(null);

  // Memoize light colors array to prevent recreation on every render
  const lightColors = useMemo(() => [
    "#C5CAE9",
    "#DCEDC8",
    "#FFECB3",
    "#F8BBD0",
    "#C8E6C9",
    "#FFD180",
    "#BBDEFB",
    "#FFCC80",
    "#B2DFDB",
  ], []);

  // Memoized fetchData function with caching to prevent redundant API calls
  const fetchData = useCallback(async () => {
    const data = { days, limit: rowsPerPage, page };
    const cacheKey = JSON.stringify(data);

    // Check if we're making the same request as last time
    if (lastFetchParams.current && JSON.stringify(lastFetchParams.current) === cacheKey) {
      return; // Skip if same parameters
    }

    // Check cache first
    if (cache.current.has(cacheKey)) {
      const cachedData = cache.current.get(cacheKey);
      setOriginalSourceNames(cachedData.source_names || []);
      const totalCount = Number(cachedData?.totalCount ?? 0);
      setTotalPages(totalCount ? Math.ceil(totalCount / rowsPerPage) : 1);
      if (cachedData.source_names?.[0] && selectedIndex === null) {
        await dispatch(selectedSourceAction(cachedData.source_names[0]));
        setSelectedIndex(0);
      }
      return;
    }

    lastFetchParams.current = data;

    try {
      const res = await dispatch(getSourcesAction(data));
      if (res) {
        // Cache the response
        //console.log("resres", res)
        cache.current.set(cacheKey, res);

        setOriginalSourceNames(res?.source_names || []);
        const totalCount = Number(res?.totalCount)
        setTotalPages(totalCount ? Math.ceil(totalCount / rowsPerPage) : 1)
        if (res?.source_names?.[0] && selectedIndex === null) {
          await dispatch(selectedSourceAction(res.source_names[0]));
          setSelectedIndex(0);
        }
      }
    } catch (err) {
      console.error("Error fetching data:", err);
    }
  }, [days, page, rowsPerPage, dispatch, selectedIndex]);

  // Memoized filtered sources - doesn't modify original data
  const filteredSourceNames = useMemo(() => {
    if (!searchTerm.trim()) {
      return originalSourceNames;
    }
    return originalSourceNames.filter((source) =>
      source.source_name.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [originalSourceNames, searchTerm]);

  // Optimized useEffect with proper dependencies and cleanup
  useEffect(() => {
    fetchData();

    // Cleanup function to clear cache when component unmounts or dependencies change
    return () => {
      // Clear old cache entries to prevent memory leaks
      if (cache.current.size > 10) {
        const entries = Array.from(cache.current.entries());
        // Keep only the 5 most recent entries
        cache.current.clear();
        entries.slice(-5).forEach(([key, value]) => {
          cache.current.set(key, value);
        });
      }
    };
  }, [fetchData]);

  // Memoized handleSelection to prevent unnecessary re-renders
  const handleSelection = useCallback((index, source) => {
    //console.log("source data",{index, source})
    setSelectedIndex(index);
    dispatch(selectedSourceAction(source));
  }, [dispatch]);
  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 0 }}>
      <List sx={{ flex: 1, minHeight: 0, overflowY: "auto", p: 0, m: 0 }}>
        {sourcesLoading ? (
          <LoadingSkeleton />
        ) : (
          filteredSourceNames?.map((source, index) => {
            const sourceLabel = `${source.source_name} (${source.source_count})`;
            return (
                  <ListItemButton
                    key={index}
                    onClick={() => handleSelection(index, source)}
                    sx={{
                      bgcolor:
                        selectedIndex === index
                          ? "#f0f8ff"
                          : "transparent",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1.5,
                        width: "100%",
                        minWidth: 0,
                      }}
                    >
                      <Avatar
                        sx={{
                          flexShrink: 0,
                          fontSize: "12px",
                          bgcolor: lightColors[index % lightColors.length],
                          color: "#000",
                        }}
                      >
                        {source.source_name.charAt(0)}
                      </Avatar>
                      <Tooltip title={sourceLabel} placement="top" arrow enterDelay={300}>
                        <Typography
                          component="span"
                          noWrap
                          sx={{
                            display: "block",
                            flex: 1,
                            minWidth: 0,
                            fontSize: "12px",
                            fontWeight: 600,
                            margin: 0,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          {sourceLabel}
                        </Typography>
                      </Tooltip>
                    </Box>
                  </ListItemButton>
            );
          })
        )}
      </List>
      <Box
        sx={{
          flexShrink: 0,
          p: 1,
          borderTop: "1px solid rgba(0, 0, 0, 0.12)",
          bgcolor: "#fff",
        }}
      >
        <Pagination
          count={totalPages}
          page={page}
          onChange={(e, value) => setPage(value)}
          disabled={sourcesLoading}
          size="small"
          siblingCount={0}
          boundaryCount={1}
          sx={{ display: "flex", justifyContent: "center", mb: 1 }}
        />
        <Box sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 1 }}>
          <Typography variant="body2">Rows per page:</Typography>
          <Select
            size="small"
            variant="standard"
            value={rowsPerPage}
            onChange={(e) => {
              setRowsPerPage(e.target.value);
              setPage(1);
            }}
            disabled={sourcesLoading}
            sx={{ minWidth: 60 }}
          >
            {[10, 20, 50, 100].map((n) => (
              <MenuItem key={n} value={n}>
                {n}
              </MenuItem>
            ))}
          </Select>
        </Box>
      </Box>
    </Box>
  );
};

export default React.memo(Sourceslist);
